import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Lock, ShieldCheck, Clock, Mail } from "lucide-react";
import { apiValidateCoupon, apiCreateOrder, apiSubscribe } from "../lib/api";
import { toast } from "sonner";

const CHECKOUT_DISABLED = process.env.REACT_APP_CHECKOUT_DISABLED === "true";

const Checkout = () => {
  const { items, subtotal, shipping, total, clearCart } = useCart();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySent, setNotifySent] = useState(false);
  const navigate = useNavigate();

  const applyCoupon = async () => {
    try {
      const r = await apiValidateCoupon(coupon, subtotal);
      setDiscount(r.discount);
      toast.success(`Coupon ${r.code} applied — saved $${r.discount.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid coupon");
      setDiscount(0);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const payload = {
      email: fd.get("email"),
      phone: fd.get("phone"),
      firstName: fd.get("firstName"),
      lastName: fd.get("lastName"),
      address: fd.get("address"),
      city: fd.get("city"),
      state: fd.get("state"),
      zip: fd.get("zip"),
      country: fd.get("country") || "USA",
      couponCode: coupon || null,
      items: items.map((i) => ({
        productId: i.id, name: i.name, sku: i.id, size: i.size,
        price: i.price, qty: i.qty, image: i.image,
      })),
    };
    try {
      const result = await apiCreateOrder(payload);
      clearCart();
      navigate(`/order/${result.orderNumber}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-bold text-[#0a0a0a]">No items to checkout</h1>
        <Button asChild className="btn-red mt-6"><Link to="/shop">Shop Now</Link></Button>
      </div>
    );
  }

  // Pre-launch mode: payment processor isn't wired yet. Show a clear notice +
  // capture the visitor's email so we can email them the moment ordering opens.
  if (CHECKOUT_DISABLED) {
    const notifyMe = async (e) => {
      e.preventDefault();
      if (!notifyEmail || !notifyEmail.includes("@")) {
        toast.error("Please enter a valid email");
        return;
      }
      try {
        await apiSubscribe(notifyEmail, "checkout-waitlist");
        setNotifySent(true);
        toast.success("You're on the launch list. We'll email you the moment ordering opens.");
      } catch {
        // even on dedupe error treat as success — the email is captured
        setNotifySent(true);
      }
    };

    return (
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16" data-testid="checkout-prelaunch">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-[#b91c1c]" />
          <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Coming Soon</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#0a0a0a] tracking-tight leading-tight">
          Online ordering opens shortly.
        </h1>
        <p className="text-gray-600 mt-4 font-light leading-relaxed max-w-2xl">
          We're in the final stages of verifying our payment processor for laboratory research materials. Your cart is saved — leave your email below and we'll send you a personal note the moment checkout goes live, along with a launch-day discount code.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-[1fr_360px] gap-10">
          <div>
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="h-5 w-5 text-[#b91c1c]" />
                <h2 className="font-display text-xl font-semibold text-[#0a0a0a]">Your order summary</h2>
              </div>
              <div className="space-y-3 max-h-72 overflow-auto pr-2">
                {items.map((i) => (
                  <div key={i.key} className="flex gap-3 text-sm">
                    <img src={i.image} alt={i.name} className="h-14 w-14 border border-gray-200 object-contain p-1 bg-white" />
                    <div className="flex-1">
                      <div className="font-semibold text-[#0a0a0a]">{i.name}</div>
                      <div className="text-xs text-gray-500">{i.size} × {i.qty}</div>
                    </div>
                    <div className="font-semibold">${(i.price * i.qty).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-1 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-gray-600"><span>Shipping</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
                <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200 mt-2"><span>Total</span><span className="text-[#0a0a0a]">${total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>

          <aside>
            {notifySent ? (
              <div className="bg-[#fafbfc] border border-gray-200 p-6 text-center" data-testid="checkout-waitlist-success">
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#0a0a0a] text-white mb-3">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="font-display text-lg font-semibold text-[#0a0a0a] mb-1">You're on the list</div>
                <p className="text-sm text-gray-600 font-light">We'll email <span className="font-semibold text-[#0a0a0a]">{notifyEmail}</span> the moment checkout opens.</p>
                <Link to="/shop" className="inline-block mt-5 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#b91c1c] hover:text-[#0a0a0a]">
                  Continue browsing →
                </Link>
              </div>
            ) : (
              <form onSubmit={notifyMe} className="bg-white border border-gray-200 p-6" data-testid="checkout-waitlist-form">
                <h3 className="font-display text-lg font-semibold text-[#0a0a0a] mb-2">Notify me on launch</h3>
                <p className="text-xs text-gray-500 font-light mb-4">Get a private launch-day promo code in your inbox.</p>
                <Label className="text-[11px] uppercase tracking-[0.18em] text-gray-500 font-semibold">Email</Label>
                <Input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  required
                  className="mt-2 rounded-none h-11"
                  placeholder="you@lab.com"
                  data-testid="checkout-waitlist-email-input"
                />
                <Button
                  type="submit"
                  className="btn-red w-full mt-4 h-11 rounded-none text-[12px] font-semibold uppercase tracking-[0.18em]"
                  data-testid="checkout-waitlist-submit-btn"
                >
                  Notify me
                </Button>
                <div className="mt-4 flex items-start gap-2 text-[11px] text-gray-500 font-light">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#b91c1c] flex-shrink-0 mt-0.5" />
                  <span>Single email when checkout opens. No spam. No third-party sharing.</span>
                </div>
              </form>
            )}
          </aside>
        </div>
      </div>
    );
  }

  const finalTotal = total - discount;

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
      <h1 className="font-display text-4xl md:text-5xl font-bold text-[#0a0a0a] uppercase">Checkout</h1>
      <div className="stripe-bar mt-3 mb-8" />
      <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
        <div className="space-y-8">
          <section className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="font-display text-xl font-bold text-[#0a0a0a] mb-4">Contact Info</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Email *</Label><Input name="email" type="email" required className="mt-1 rounded-none" /></div>
              <div><Label>Phone *</Label><Input name="phone" required className="mt-1 rounded-none" /></div>
            </div>
          </section>
          <section className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="font-display text-xl font-bold text-[#0a0a0a] mb-4">Shipping Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>First Name *</Label><Input name="firstName" required className="mt-1 rounded-none" /></div>
              <div><Label>Last Name *</Label><Input name="lastName" required className="mt-1 rounded-none" /></div>
              <div className="md:col-span-2"><Label>Address *</Label><Input name="address" required className="mt-1 rounded-none" /></div>
              <div><Label>City *</Label><Input name="city" required className="mt-1 rounded-none" /></div>
              <div><Label>State *</Label><Input name="state" required className="mt-1 rounded-none" /></div>
              <div><Label>ZIP *</Label><Input name="zip" required className="mt-1 rounded-none" /></div>
              <div><Label>Country *</Label><Input name="country" required defaultValue="USA" className="mt-1 rounded-none" /></div>
            </div>
          </section>
          <section className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="font-display text-xl font-bold text-[#0a0a0a] mb-4 flex items-center gap-2"><Lock className="h-4 w-4" /> Payment</h2>
            <div className="bg-[#fafbfc] border-l-4 border-[#b91c1c] p-4 mb-4 text-[12px] text-gray-700">
              <strong className="text-[#0a0a0a] uppercase tracking-[0.1em] text-[11px]">Stripe Secure Checkout</strong>
              <p className="mt-1 font-light">Card details are handled by Stripe over a 256-bit SSL connection. We never see or store your full card number.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Label>Card Number *</Label><Input required placeholder="1234 5678 9012 3456" className="mt-1" /></div>
              <div><Label>Expiry *</Label><Input required placeholder="MM/YY" className="mt-1" /></div>
              <div><Label>CVC *</Label><Input required placeholder="123" className="mt-1" /></div>
            </div>
            <p className="text-xs text-gray-500 mt-3 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Secure 256-bit SSL encrypted</p>
          </section>
        </div>

        <aside className="space-y-4 h-fit lg:sticky lg:top-32">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="font-display text-lg font-bold text-[#0a0a0a] mb-4">Your Order</h3>
            <div className="space-y-3 max-h-64 overflow-auto pr-2">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3 text-sm">
                  <img src={i.image} alt={i.name} className="h-14 w-14 rounded border border-gray-200 object-contain p-1 bg-white" />
                  <div className="flex-1">
                    <div className="font-semibold text-[#0a0a0a]">{i.name}</div>
                    <div className="text-xs text-gray-500">{i.size} × {i.qty}</div>
                  </div>
                  <div className="font-semibold">${(i.price * i.qty).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" />
              <Button type="button" onClick={applyCoupon} variant="outline">Apply</Button>
            </div>
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
              {discount > 0 && (<div className="flex justify-between text-[#b91c1c]"><span>Discount</span><span>-${discount.toFixed(2)}</span></div>)}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200 mt-2"><span>Total</span><span className="text-[#b91c1c]">${finalTotal.toFixed(2)}</span></div>
            </div>
            <Button type="submit" disabled={submitting} className="btn-red w-full mt-5 h-12 font-bold uppercase tracking-wide" data-testid="place-order-btn">
              {submitting ? "Placing order…" : "Place Order"}
            </Button>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default Checkout;
